<template>
    <dialog-modal
        v-if="shouldRender"
        :is-processing="form.processing"
        :primary-button-label="trans('general.action.yes')"
        :show="show"
        :submit-disabled="!canSubmit"
        @close="closeModal"
        @submit="handleSubmit"
    >
        <template #title>
            <asset-short-info v-if="headerAsset" :asset="headerAsset" class="my-4">
                <template #top>
                    <status-badge :enable-mobile="true" :operability="headerAsset.status?.operability" />
                </template>
            </asset-short-info>
        </template>

        <div
            v-html="
                trans(`workorder.modal.changes.asses.status.operable.description`, {
                    operability: operabilityLabel,
                    reason: reasonLabel,
                })
            "
        />
        <form @submit.prevent="handleSubmit">
            <row v-if="attestationRequired" class="mt-3">
                <column>
                    <jet-checkbox
                        v-model="form.tested_and_calibrated"
                        :invalid-feedback="form.errors.tested_and_calibrated"
                        :label="trans('assets.modal.operability_change.attestation.label')"
                    />
                </column>
            </row>
            <row class="mt-3">
                <column>
                    <jet-text-area
                        v-model="form.additionalInformation"
                        :horizontal="false"
                        :invalidFeedback="form.errors.additionalInformation"
                        :label="trans('assets.modal.operability_change.additional_information.label')"
                        :maxlength="80"
                        :placeholder="trans('assets.modal.operability_change.additional_information.placeholder')"
                        :required="form.operability === 'inoperable'"
                        :rows="4"
                        name="additional_information"
                    />
                </column>
            </row>
        </form>
        <required-fields />
    </dialog-modal>
</template>
<script setup>
import DialogModal from '@/Jetstream/DialogModal.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetTextArea from '@/Jetstream/Textarea.vue';
import { eventBus, events } from '@/eventBus.js';
import { $http } from '@/bootstrap.js';
import { trans } from '@/mixins/i18n.js';
import RequiredFields from '@/Components/RequiredFields.vue';
import AssetShortInfo from '@/Components/AssetShortInfo.vue';
import StatusBadge from '@/Components/StatusBadge.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useForm } from '@inertiajs/vue3';

const show = ref(false);
const shouldRender = ref(false);
const workOrderId = ref(null);
const status = ref(null);
const assetId = ref(null);
const headerAsset = ref(null);
const reintegrationAttestationRequired = ref(false);

const form = useForm({
    operability: 'inoperable',
    additionalInformation: '',
    reason: 'corrective_maintenance_required',
    tested_and_calibrated: false,
});

const attestationRequired = computed(() => reintegrationAttestationRequired.value && form.operability === 'operable');

const canSubmit = computed(() => {
    if (!form.isDirty) {
        return false;
    }
    if (form.operability === 'inoperable' && !form.additionalInformation?.trim()) {
        return false;
    }
    if (attestationRequired.value && form.tested_and_calibrated !== true) {
        return false;
    }
    return true;
});

const operabilityLabel = computed(() => {
    const text = trans(`assets.modal.operability_change.label.${form.operability}`);
    const cssClass = form.operability === 'inoperable' ? 'bg-danger' : form.operability === 'operable' ? 'bg-success' : 'bg-secondary';

    return `<span><em
            class="operability-indicator ${cssClass} ${form.operability}"></em>&nbsp;<b>${text}</b></span>`;
});

const reasonLabel = computed(() => trans(`assets.modal.operability_change.reason.${form.reason}`));
const handleWorkOrderStatusUpdate = ({ workOrderId: id, status: s }) => {
    resetForm();
    workOrderId.value = id;
    status.value = s;

    checkWorkOrderStatus();
};

const handleOpenAssetStatusChangePrompt = (data) => {
    resetForm();
    assetId.value = data.assetId;
    headerAsset.value = data.asset ?? null;
    form.operability = 'inoperable';
    form.reason = 'corrective_maintenance';
    openModal();
};

onMounted(() => {
    eventBus.$on(events.workOrderStatusUpdated, handleWorkOrderStatusUpdate);
    eventBus.$on(events.openAssetStatusChangePrompt, handleOpenAssetStatusChangePrompt);
});

onBeforeUnmount(() => {
    eventBus.$off(events.workOrderStatusUpdated, handleWorkOrderStatusUpdate);
    eventBus.$off(events.openAssetStatusChangePrompt, handleOpenAssetStatusChangePrompt);
});

const resetForm = () => {
    form.defaults({
        operability: 'inoperable',
        additionalInformation: '',
        reason: 'corrective_maintenance_required',
        tested_and_calibrated: false,
    });

    form.reset();
    form.clearErrors();
    reintegrationAttestationRequired.value = false;
};

const transformData = (data) => {
    data.additionalInformation = data.additionalInformation || null;
    data.workOrderId = workOrderId.value || null;

    if (attestationRequired.value) {
        data.tested_and_calibrated = data.tested_and_calibrated === true;
    } else {
        delete data.tested_and_calibrated;
    }

    switch (data.reason) {
        case 'corrective_maintenance':
            data.reason = 'corrective_maintenance_required';
            break;
        case 'preventative_maintenance':
            data.reason = 'preventative_maintenance_in_progress';
            break;
        default:
            delete data.reason;
    }

    return data;
};

const handleSubmit = () => {
    form.transform(transformData).post(route('assets.update-operability', assetId.value), {
        preserveState: true,
        onSuccess: () => {
            eventBus.$emit(events.updateNotifications);

            eventBus.$emit(events.assetOperabilityUpdated, {
                asset: assetId.value,
                ...form.data(),
            });

            closeModal();
        },
    });
};

const openModal = () => {
    shouldRender.value = true;
    nextTick(() => (show.value = true));
};

const closeModal = () => {
    show.value = false;
    setTimeout(() => {
        shouldRender.value = false;
        resetForm();
        workOrderId.value = null;
        status.value = null;
        assetId.value = null;
        headerAsset.value = null;
    }, 300);
};

const checkWorkOrderStatus = () => {
    $http.get(route('workorders.status.show', workOrderId.value)).then((response) => {
        const { workOrder, asset } = response.data;

        if (!asset || !!asset.deletedAt) {
            return;
        }

        if (status.value === 'to_do') {
            return;
        }

        const operability = asset.operability.value;

        // Don't prompt to reintegrate an inoperable asset while non-op corrective
        // work orders are still open — same end state as the operator cancelling.
        if (operability === 'inoperable' && asset.hasBlockingCorrectiveWorkOrders === true) {
            return;
        }

        switch (`${status.value}:${operability}`) {
            case 'in_progress:operable':
            case 'resolved:inoperable':
            case 'closed:inoperable':
            case 'done:inoperable':
                form.operability = operability === 'operable' ? 'inoperable' : 'operable';
                assetId.value = asset.id;
                headerAsset.value = {
                    id: asset.id,
                    name: asset.fullLabel,
                    curieId: asset.curieId,
                    serial_number: asset.serialNumber,
                    auxiliaryHardwareSerialNumber: asset.auxiliaryHardwareSerialNumber,
                    location: asset.location,
                    status: { operability },
                };
                form.reason = workOrder.type.value;
                reintegrationAttestationRequired.value = asset.reintegrationAttestationRequired === true;
                openModal();
                break;
        }
    });
};
</script>
<style scoped>
:deep(textarea) {
    resize: none;
}
</style>
